import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import BanIcon from '@heroicons/react/outline/BanIcon';
import ChartBarIcon from '@heroicons/react/outline/ChartBarIcon';
import ExclamationIcon from '@heroicons/react/outline/ExclamationIcon';
import KeyIcon from '@heroicons/react/outline/KeyIcon';
import MailIcon from '@heroicons/react/outline/MailIcon';
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon';
import ShareIcon from '@heroicons/react/outline/ShareIcon';
import ShieldCheckIcon from '@heroicons/react/outline/ShieldCheckIcon';
import ShieldExclamationIcon from '@heroicons/react/outline/ShieldExclamationIcon';
import TrashIcon from '@heroicons/react/outline/TrashIcon';
import UserIcon from '@heroicons/react/outline/UserIcon';
import { hasScopesPermission } from '@tribeplatform/gql-client/lib';
import { MemberEmailStatus, MemberStatus, ModerationEntityType, NetworkVisibility, RoleType, } from '@tribeplatform/gql-client/types';
import { useAuthMember, useAuthToken, useDeleteMember, useNetworkRoles, useSendResetPasswordEmail, useSuspendMember, useUnsuspendMember, useVerifyMember, } from '@tribeplatform/react-sdk/hooks';
import { confirm } from '@tribeplatform/react-ui-kit/Dialog';
import { toast } from '@tribeplatform/react-ui-kit/Toast';
import { MemberAnalyticsModal } from '../../Analytics/MemberAnalytics';
import { AuditLogsModal } from '../../AuditLog';
import { useI18n } from '../../i18n';
import { ManageBadgesModal } from '../../MemberBadge';
import { ProfileSettingsModal } from '../../ProfileSettingsModal';
import { ReportModal } from '../../ReportModal';
import { ShareModal } from '../../ShareModal';
import { useFlagMemberActionItem } from './useFlagMemberActionItem';
export const useMemberActionItems = ({ member, context, }) => {
    var _a, _b, _c, _d;
    const { $t } = useI18n();
    const actionItems = [];
    const [shareModal, setShareModal] = useState(false);
    const [editModal, setEditModal] = useState(false);
    const [auditLogsModal, setAuditLogsModal] = useState(false);
    const [assignBadgesModal, setAssignBadgesModal] = useState(false);
    const [reportModal, setReportModal] = useState(false);
    const [memberAnalyticsModal, setMemberAnalyticsModal] = useState(false);
    const { data: authMember } = useAuthMember();
    const { data: { network }, } = useAuthToken();
    const roles = useNetworkRoles();
    const { mutateAsync: sendResetPasswordEmail } = useSendResetPasswordEmail();
    const { mutateAsync: verifyEmail } = useVerifyMember();
    const { mutateAsync: deleteMember } = useDeleteMember();
    const { mutateAsync: suspendMember } = useSuspendMember();
    const { mutateAsync: unsuspendMember } = useUnsuspendMember();
    // TODO (refactor): this is how we want to handle the action items
    const flagMemberActionItem = useFlagMemberActionItem(member);
    const [canUpdateMember, canSuspendMember, canUnsuspendMember, canRemoveMember,] = hasScopesPermission(member, [
        'updateMember',
        'suspendMember',
        'unsuspendMember',
        'removeMember',
    ]);
    const [canUpdateNetwork, canAssignBadge, canSendResetPasswordEmail, canVerifyMember, canReportMember,] = hasScopesPermission(network, [
        'updateNetwork',
        'assignBadge',
        'sendResetPasswordEmail',
        'verifyMember',
        'createModerationReport',
    ]);
    if (!(member === null || member === void 0 ? void 0 : member.id)) {
        return { actions: [], children: null };
    }
    const memberPath = member === null || member === void 0 ? void 0 : member.relativeUrl;
    const isOwnProfile = (authMember === null || authMember === void 0 ? void 0 : authMember.id) === (member === null || member === void 0 ? void 0 : member.id);
    const onDelete = async (member) => {
        if (!(member === null || member === void 0 ? void 0 : member.id)) {
            return;
        }
        const confirmed = await confirm({
            title: $t({
                defaultMessage: 'Are you sure you want to remove {member_name} ({member_username})?',
                id: 'MemberAction.Delete.Confirmation',
            }, { member_name: member.name, member_username: member.username }),
            proceedLabel: $t({
                id: 'Generics.Remove',
                defaultMessage: 'Remove',
            }),
            cancelLabel: $t({
                defaultMessage: 'Cancel',
                id: 'Generics.Cancel',
            }),
            danger: true,
        });
        if (!confirmed) {
            return;
        }
        try {
            await deleteMember({ id: member === null || member === void 0 ? void 0 : member.id });
            toast({
                title: $t({
                    defaultMessage: '{MEMBER_CC} removed',
                    id: 'MemberAction.Removed',
                }),
                description: $t({
                    defaultMessage: '{MEMBER_CC} is successfully removed.',
                    id: 'MemberAction.Removed.Description',
                }),
                status: 'success',
            });
        }
        catch (err) {
            toast({
                title: $t({
                    id: 'Generics.SomethingWentWrong',
                    defaultMessage: 'Something went wrong',
                }),
                description: $t({
                    defaultMessage: 'Cannot remove this {MEMBER}.',
                    id: 'MemberAction.FailedToRemove.Description',
                }),
                status: 'error',
            });
        }
    };
    const onSuspend = async (member) => {
        if (!(member === null || member === void 0 ? void 0 : member.id)) {
            return;
        }
        const confirmed = await confirm({
            title: $t({
                defaultMessage: '{MEMBER} suspension',
                id: 'MemberAction.Suspend.Confirmation',
            }),
            description: $t({
                defaultMessage: 'Suspending a {MEMBER} will take away their ability to login and engage with your {COMMUNITY} as a registered {MEMBER}. Please note that you will be able to revoke the suspension if you will choose to do so later on.',
                id: 'MemberAction.Suspend.Confirmation.Description',
            }),
            proceedLabel: $t({
                defaultMessage: 'Suspend',
                id: 'Generics.Suspend',
            }),
            cancelLabel: $t({
                defaultMessage: 'Cancel',
                id: 'Generics.Cancel',
            }),
        });
        if (!confirmed) {
            return;
        }
        try {
            await suspendMember({ memberId: member === null || member === void 0 ? void 0 : member.id });
            toast({
                title: $t({
                    defaultMessage: '{MEMBER_CC} suspended',
                    id: 'MemberAction.Suspended',
                }),
                description: $t({
                    defaultMessage: '{MEMBER_CC} is successfully suspended.',
                    id: 'MemberAction.Suspended.Description',
                }),
                status: 'success',
            });
        }
        catch (err) {
            toast({
                title: $t({
                    defaultMessage: 'Something went wrong',
                    id: 'Generics.SomethingWentWrong',
                }),
                description: $t({
                    defaultMessage: 'Cannot suspend this {MEMBER}.',
                    id: 'MemberAction.FailedToSuspend.Description',
                }),
                status: 'error',
            });
        }
    };
    const onUnsuspend = async (member) => {
        if (!(member === null || member === void 0 ? void 0 : member.id)) {
            return;
        }
        try {
            await unsuspendMember({ memberId: member === null || member === void 0 ? void 0 : member.id });
            toast({
                title: $t({
                    defaultMessage: '{MEMBER_CC} suspension revoked',
                    id: 'MemberAction.RevokedSuspension',
                }),
                description: $t({
                    defaultMessage: '{MEMBER_CC} suspension is successfully revoked.',
                    id: 'MemberAction.RevokedSuspension.Description',
                }),
                status: 'success',
            });
        }
        catch (err) {
            toast({
                title: $t({
                    defaultMessage: 'Something went wrong',
                    id: 'Generics.SomethingWentWrong',
                }),
                description: $t({
                    defaultMessage: 'Cannot revoke suspension for this {MEMBER}.',
                    id: 'MemberAction.FailedToRevokeSuspension.Description',
                }),
                status: 'error',
            });
        }
    };
    const onSendResetPasswordEmail = async (member) => {
        if (!(member === null || member === void 0 ? void 0 : member.id)) {
            return;
        }
        const confirmed = await confirm({
            title: $t({
                defaultMessage: 'Reset password',
                id: 'Generics.ResetPassword',
            }),
            description: $t({
                defaultMessage: 'An email containing reset password link will be sent to the {MEMBER}. Are you sure you want to continue?',
                id: 'Member.ResetPassword.Confirmation.Description',
            }),
            proceedLabel: $t({
                defaultMessage: 'Email reset password link',
                id: 'Member.ResetPassword.Confirmation.Proceed',
            }),
            cancelLabel: $t({
                defaultMessage: 'Cancel',
                id: 'Generics.Cancel',
            }),
        });
        if (!confirmed) {
            return;
        }
        try {
            await sendResetPasswordEmail({ email: member === null || member === void 0 ? void 0 : member.email });
            toast({
                title: $t({
                    defaultMessage: 'Email sent',
                    id: 'MemberAction.ResetPassSent',
                }),
                description: $t({
                    defaultMessage: 'Reset password email sent to the {MEMBER}.',
                    id: 'MemberAction.ResetPassSent.Description',
                }),
                status: 'success',
            });
        }
        catch (err) {
            toast({
                title: $t({
                    defaultMessage: 'Something went wrong',
                    id: 'Generics.SomethingWentWrong',
                }),
                description: $t({
                    defaultMessage: 'Cannot send reset password email.',
                    id: 'MemberAction.ResetPassFailedToSend.Description',
                }),
                status: 'error',
            });
        }
    };
    const onVerifyEmail = async (memberId) => {
        const confirmed = await confirm({
            title: $t({
                id: 'MemberAction.VerifyEmail.Confirmation',
                defaultMessage: 'Are you sure you want to manually confirm {MEMBER}’s email address?',
            }),
            description: $t({
                id: 'MemberAction.VerifyEmail.Confirmation.Description',
                defaultMessage: 'Confirming incorrect email addresses can hurt your email delivery rate.',
            }),
            proceedLabel: $t({
                id: 'Generics.Yes',
                defaultMessage: 'Yes',
            }),
            cancelLabel: $t({
                id: 'Generics.No',
                defaultMessage: 'No',
            }),
        });
        if (!confirmed) {
            return;
        }
        await verifyEmail({
            input: {
                memberId,
            },
        });
        toast({
            title: $t({
                defaultMessage: 'Email address verified',
                id: 'MemberAction.EmailVerified',
            }),
            description: $t({
                defaultMessage: 'The email address is now verified.',
                id: 'MemberAction.EmailVerified.Description',
            }),
            status: 'success',
        });
    };
    const categoryText = $t({
        id: 'Generics.Member',
        defaultMessage: '{MEMBER_CC}',
    });
    if (context === 'admin') {
        actionItems.push({
            url: memberPath,
            icon: UserIcon,
            name: $t({
                id: 'Generics.ViewProfile',
                defaultMessage: 'View profile',
            }),
            category: categoryText,
        });
    }
    if (canUpdateMember && context !== 'member') {
        actionItems.push({
            action: () => setEditModal(true),
            icon: PencilAltIcon,
            name: $t({ defaultMessage: 'Edit profile', id: 'Generics.EditProfile' }),
            category: categoryText,
        });
    }
    actionItems.push({
        action: () => setShareModal(true),
        icon: ShareIcon,
        name: $t({
            defaultMessage: 'Share {MEMBER}',
            id: 'Member.Share',
        }),
        hotkey: 'option+s,alt+s',
        category: categoryText,
    });
    if (canAssignBadge) {
        actionItems.push({
            action: () => setAssignBadgesModal(true),
            icon: ShieldCheckIcon,
            name: $t({
                defaultMessage: 'Manage badges',
                id: 'Generics.ManageBadges',
            }),
            category: categoryText,
        });
    }
    if (canUpdateNetwork) {
        actionItems.push({
            action: () => setMemberAnalyticsModal(true),
            icon: ChartBarIcon,
            name: $t({
                defaultMessage: '{MEMBER_CC} analytics',
                id: 'Member.Analytics',
            }),
            hotkey: 'option+a,alt+a',
            category: categoryText,
        });
    }
    if (canUpdateNetwork) {
        actionItems.push({
            action: () => setAuditLogsModal(true),
            icon: ShieldExclamationIcon,
            name: $t({
                id: 'Generics.AuditLogs',
                defaultMessage: 'Audit logs',
            }),
            hotkey: 'option+l,alt+l',
            category: categoryText,
        });
    }
    if ((authMember === null || authMember === void 0 ? void 0 : authMember.role.type) === RoleType.admin && canSendResetPasswordEmail) {
        actionItems.push({
            action: () => onSendResetPasswordEmail(member),
            icon: KeyIcon,
            name: $t({
                id: 'Generics.ResetPassword',
                defaultMessage: 'Reset password',
            }),
            category: categoryText,
        });
    }
    if (!isOwnProfile &&
        (authMember === null || authMember === void 0 ? void 0 : authMember.role.type) === RoleType.admin &&
        member.emailStatus !== MemberEmailStatus.verified &&
        canVerifyMember) {
        actionItems.push({
            action: () => onVerifyEmail(member === null || member === void 0 ? void 0 : member.id),
            icon: MailIcon,
            name: $t({
                id: 'Member.VerifyEmail',
                defaultMessage: 'Verify email address',
            }),
            category: categoryText,
        });
    }
    if (flagMemberActionItem) {
        actionItems.push(flagMemberActionItem);
    }
    if (((_a = member === null || member === void 0 ? void 0 : member.role) === null || _a === void 0 ? void 0 : _a.type) !== ((_b = roles === null || roles === void 0 ? void 0 : roles.admin) === null || _b === void 0 ? void 0 : _b.type) &&
        ((_c = member === null || member === void 0 ? void 0 : member.role) === null || _c === void 0 ? void 0 : _c.type) !== ((_d = roles === null || roles === void 0 ? void 0 : roles.moderator) === null || _d === void 0 ? void 0 : _d.type) &&
        canSuspendMember &&
        !isOwnProfile &&
        (member === null || member === void 0 ? void 0 : member.status) !== MemberStatus.Suspended) {
        actionItems.push({
            action: () => onSuspend(member),
            icon: BanIcon,
            name: $t({
                id: 'Member.Suspend',
                defaultMessage: 'Suspend {MEMBER}',
            }),
            category: categoryText,
        });
    }
    if (canUnsuspendMember &&
        (member === null || member === void 0 ? void 0 : member.status) === MemberStatus.Suspended &&
        !isOwnProfile) {
        actionItems.push({
            action: () => onUnsuspend(member),
            icon: BanIcon,
            name: $t({
                id: 'Member.RevokeSuspension',
                defaultMessage: 'Revoke suspension',
            }),
            category: categoryText,
        });
    }
    if (canRemoveMember && !isOwnProfile) {
        actionItems.push({
            action: () => onDelete(member),
            icon: TrashIcon,
            name: $t({
                id: 'Member.Remove',
                defaultMessage: 'Remove {MEMBER}',
            }),
            hotkey: 'cmd+backspace,ctrl+backspace',
            category: categoryText,
        });
    }
    if (canReportMember) {
        actionItems.push({
            action: () => setReportModal(true),
            icon: ExclamationIcon,
            name: $t({
                id: 'Member.Report',
                defaultMessage: 'Report {MEMBER}',
            }),
            category: categoryText,
        });
    }
    const children = (_jsxs(_Fragment, { children: [_jsx(ShareModal, { isPrivate: network.visibility === NetworkVisibility.private, memberId: member === null || member === void 0 ? void 0 : member.id, open: shareModal, onClose: () => setShareModal(false), title: member === null || member === void 0 ? void 0 : member.name, url: `https://${network === null || network === void 0 ? void 0 : network.domain}${memberPath}` }, void 0), editModal ? (_jsx(ProfileSettingsModal, { member: member, open: true, onClose: () => setEditModal(false) }, void 0)) : null, auditLogsModal && (_jsx(AuditLogsModal, { open: true, onClose: () => setAuditLogsModal(false), memberId: member === null || member === void 0 ? void 0 : member.id }, void 0)), assignBadgesModal && (_jsx(ManageBadgesModal, { open: true, onClose: () => setAssignBadgesModal(false), memberId: member === null || member === void 0 ? void 0 : member.id }, void 0)), memberAnalyticsModal && (_jsx(MemberAnalyticsModal, { open: true, onClose: () => setMemberAnalyticsModal(false), member: member, linkToContent: false }, void 0)), reportModal && (_jsx(ReportModal, { entity: member, entityType: ModerationEntityType.MEMBER, onClose: () => setReportModal(false), open: true }, void 0))] }, void 0));
    return { actions: actionItems, children };
};
//# sourceMappingURL=useMemberActionItems.js.map